import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { eur, fmtDate } from "../helpers";
import { Field } from "../components/FormField";
import { Wallet, Percent, Sparkles, Wrench, TrendingUp, Home, Landmark, FileText, FileDown } from "lucide-react";

const ADMIN_ROLES = ["super_admin", "admin", "amministratore"];
const now = new Date();

export default function OwnerStatements() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [owners, setOwners] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (isAdmin) api.get("/owners").then((r) => { setOwners(r.data); if (r.data[0]) setOwnerId(r.data[0].id); });
    api.get("/owner-reports").then((r) => setReports(r.data)).catch(() => setReports([]));
  }, [isAdmin]);

  const downloadReport = async (rep) => {
    try {
      const res = await api.get(`/owner-reports/${rep.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `report_${rep.year}_${String(rep.month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };
  const monthName = (m) => new Date(2020, m - 1, 1).toLocaleString(lang === "it" ? "it-IT" : "en-US", { month: "long" });

  const fetchStatement = useCallback(() => {
    if (isAdmin && !ownerId) return;
    const params = { year, month };
    if (isAdmin) params.owner_id = ownerId;
    setErr("");
    api.get("/owner-statement", { params })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setErr(e.response?.data?.detail || t("noData")); });
  }, [isAdmin, ownerId, year, month, t]);

  useEffect(() => { fetchStatement(); }, [fetchStatement]);

  const monthLabel = (m) => new Date(2020, m - 1, 1).toLocaleString(lang === "it" ? "it-IT" : "en-US", { month: "long" });
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  const tot = data?.totals;

  const cards = tot ? [
    { icon: Wallet, label: t("grossRevenue"), value: eur(tot.gross), tone: "bg-primary text-primary-foreground" },
    { icon: Percent, label: `${t("commissionAmount")} (${data.owner.commission_pct}%)`, value: eur(tot.commission), tone: "bg-violet-100 text-violet-700" },
    { icon: Sparkles, label: t("cleaningCosts"), value: eur(tot.cleaning_costs), tone: "bg-cyan-100 text-cyan-700" },
    { icon: Wrench, label: t("maintenanceCosts"), value: eur(tot.maintenance_costs), tone: "bg-amber-100 text-amber-700" },
    { icon: Landmark, label: t("netPayout"), value: eur(tot.net), tone: "bg-emerald-100 text-emerald-700" },
    { icon: TrendingUp, label: t("occupancy"), value: `${tot.occupancy}%`, tone: "bg-slate-100 text-slate-700" },
  ] : [];

  return (
    <div className="space-y-6 fade-in" data-testid="statements-page">
      <Card className="flex flex-wrap items-end gap-4 border-slate-200 p-5">
        {isAdmin && (
          <Field label={t("selectOwner")} className="min-w-[220px]">
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger data-testid="statement-owner"><SelectValue placeholder={t("selectOwner")} /></SelectTrigger>
              <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <Field label={t("month")} className="min-w-[150px]">
          <Select value={String(month)} onValueChange={(v) => setMonth(+v)}>
            <SelectTrigger data-testid="statement-month"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <SelectItem key={m} value={String(m)} className="capitalize">{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("year")} className="min-w-[110px]">
          <Select value={String(year)} onValueChange={(v) => setYear(+v)}>
            <SelectTrigger data-testid="statement-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </Card>

      {(() => {
        const list = isAdmin ? reports.filter((r) => r.owner_id === ownerId) : reports;
        return list.length > 0 ? (
          <Card className="border-slate-200 p-5" data-testid="owner-reports-card">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><FileText size={16} /> {t("monthlyReports")}</div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((r) => (
                <div key={r.id} data-testid={`stmt-report-${r.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                  <div className="text-sm capitalize text-slate-700">{monthName(r.month)} {r.year}</div>
                  <Button size="sm" variant="outline" className="gap-1" data-testid={`stmt-download-${r.id}`} onClick={() => downloadReport(r)}><FileDown size={14} /> PDF</Button>
                </div>
              ))}
            </div>
          </Card>
        ) : null;
      })()}

      {err && <div data-testid="statement-error" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{err === "owner_id richiesto" ? t("noStatementOwner") : err}</div>}

      {data && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-head text-lg font-semibold text-slate-900" data-testid="statement-owner-name">{data.owner.name}</div>
              <div className="text-xs text-slate-500 capitalize">{monthLabel(data.period.month)} {data.period.year}{data.owner.iban ? ` · IBAN ${data.owner.iban}` : ""}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            {cards.map((c) => (
              <Card key={c.label} className="flex items-center gap-4 border-slate-200 p-5">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${c.tone}`}><c.icon size={20} /></div>
                <div><div className="text-2xl font-semibold text-slate-900 font-head">{c.value}</div><div className="text-xs text-slate-500">{c.label}</div></div>
              </Card>
            ))}
          </div>

          <Card className="overflow-hidden border-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 flex items-center gap-2"><Home size={16} /> {t("apartments")}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">{t("apartment")}</th>
                    <th className="px-4 py-3">{t("nights")}</th>
                    <th className="px-4 py-3">{t("occupancy")}</th>
                    <th className="px-4 py-3">{t("grossRevenue")}</th>
                    <th className="px-4 py-3">{t("commissionAmount")}</th>
                    <th className="px-4 py-3">{t("cleaningCosts")}</th>
                    <th className="px-4 py-3">{t("maintenanceCosts")}</th>
                    <th className="px-4 py-3">{t("netPayout")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.apartments.map((a) => (
                    <tr key={a.apartment_id} data-testid={`statement-apt-${a.apartment_id}`} className="border-b border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
                      <td className="px-4 py-3 text-slate-600">{a.nights}</td>
                      <td className="px-4 py-3 text-slate-600">{a.occupancy}%</td>
                      <td className="px-4 py-3 text-slate-600">{eur(a.gross)}</td>
                      <td className="px-4 py-3 text-violet-700">{eur(a.commission)}</td>
                      <td className="px-4 py-3 text-cyan-700">{eur(a.cleaning_costs)}</td>
                      <td className="px-4 py-3 text-amber-700">{eur(a.maintenance_costs)}</td>
                      <td className="px-4 py-3 font-semibold text-emerald-700">{eur(a.net)}</td>
                    </tr>
                  ))}
                  {data.apartments.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
                </tbody>
                {tot && data.apartments.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-900">
                      <td className="px-4 py-3">{t("totalRevenue")}</td>
                      <td className="px-4 py-3">{tot.nights}</td>
                      <td className="px-4 py-3">{tot.occupancy}%</td>
                      <td className="px-4 py-3">{eur(tot.gross)}</td>
                      <td className="px-4 py-3 text-violet-700">{eur(tot.commission)}</td>
                      <td className="px-4 py-3 text-cyan-700">{eur(tot.cleaning_costs)}</td>
                      <td className="px-4 py-3 text-amber-700">{eur(tot.maintenance_costs)}</td>
                      <td className="px-4 py-3 text-emerald-700">{eur(tot.net)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>

          <Card className="overflow-hidden border-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">{t("reservations")}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">{t("guestName")}</th>
                    <th className="px-4 py-3">{t("apartment")}</th>
                    <th className="px-4 py-3">{t("checkIn")}</th>
                    <th className="px-4 py-3">{t("checkOut")}</th>
                    <th className="px-4 py-3">{t("channel")}</th>
                    <th className="px-4 py-3">{t("totalAmount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bookings.map((b, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-900">{b.guest_name}</td>
                      <td className="px-4 py-3 text-slate-600">{b.apartment_name}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(b.check_in)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(b.check_out)}</td>
                      <td className="px-4 py-3 text-slate-600 capitalize">{b.channel}</td>
                      <td className="px-4 py-3 text-slate-600">{eur(b.total_amount)}</td>
                    </tr>
                  ))}
                  {data.bookings.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
